import React from 'react';
import { 
  Box, 
  Card, 
  Typography, 
  Chip, 
  useTheme, 
  alpha,
  CircularProgress,
  LinearProgress
} from '@mui/material';

/**
 * A modern metric card component for displaying ESDL monitoring metrics
 * 
 * @param {Object} props
 * @param {string} props.title - The title of the metric card
 * @param {string} props.value - The primary value to display
 * @param {string} props.subtitle - The subtitle or description
 * @param {React.Component} props.icon - The icon component to display
 * @param {string} props.color - The color theme ('primary', 'success', 'warning', 'error', or 'info')
 * @param {number} props.progress - Optional progress value (0-100) for progress indicator
 * @param {Array} props.chips - Optional array of chip objects with label, color, and icon
 * @param {function} props.onClick - Optional click handler for the card
 * @param {boolean} props.loading - Whether the card is in loading state
 */
const EsdlMetricCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = 'primary',
  progress,
  chips = [],
  onClick,
  loading = false
}) => {
  const theme = useTheme();
  
  // Get color based on theme
  const getColorValue = (colorName) => {
    if (colorName === 'quaternary') {
      return '#ff9966'; // Custom color not in MUI palette
    }
    return theme.palette[colorName]?.main || theme.palette.primary.main;
  };
  
  // Get corresponding text color
  const getTextColor = (colorName) => {
    return theme.palette[colorName]?.contrastText || theme.palette.primary.contrastText;
  };
  
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        background: `linear-gradient(145deg, ${alpha(getColorValue(color), 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
        backdropFilter: 'blur(8px)',
        transition: 'all 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: `0 15px 30px ${alpha(theme.palette.common.black, 0.12)}, 0 0 10px ${alpha(getColorValue(color), 0.2)}`,
          '& .icon-bg': {
            transform: 'scale(1.1)',
          },
          borderColor: alpha(getColorValue(color), 0.3),
          backgroundColor: alpha(getColorValue(color), 0.02),
          '& .card-title': {
            color: getColorValue(color)
          },
          '& .card-icon': {
            transform: 'scale(1.1)',
            backgroundColor: alpha(getColorValue(color), 0.15)
          },
          '& .card-value': {
            textShadow: `0 0 8px ${alpha(getColorValue(color), 0.3)}`,
            transform: 'scale(1.05)',
            transformOrigin: 'left'
          }
        } : {},
        border: '1px solid transparent',
      }}
      onClick={onClick}
    >
      {/* Background decoration */}
      <Box 
        className="icon-bg"
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${alpha(getColorValue(color), 0.05)} 0%, ${alpha(getColorValue(color), 0.02)} 100%)`,
          transition: 'transform 0.3s ease',
        }}
      />
      
      {/* Loading overlay */}
      {loading && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.paper, 0.7),
            zIndex: 10 
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}
      
      {/* Content */}
      <Box sx={{ position: 'relative', height: '100%', p: 2, pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <Box>
            <Typography 
              variant="subtitle1" 
              fontWeight="medium" 
              className="card-title"
              sx={{ 
                color: theme.palette.text.primary,
                transition: 'color 0.3s ease'
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: alpha(theme.palette.text.secondary, 0.8),
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {Icon && (
            <Box 
              className="card-icon"
              sx={{
                backgroundColor: alpha(getColorValue(color), 0.1),
                color: getColorValue(color),
                borderRadius: 2,
                width: 42,
                height: 42,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              <Icon sx={{ fontSize: 22 }} />
            </Box>
          )}
        </Box>
        
        {/* Main value */}
        <Typography 
          variant="h3" 
          fontWeight="bold"
          className="card-value" 
          sx={{ 
            color: getColorValue(color),
            fontSize: '2rem',
            mb: 1.5,
            transition: 'all 0.3s ease'
          }}
        >
          {value}
        </Typography>
        
        {/* Progress bar if provided */}
        {typeof progress === 'number' && (
          <Box mb={1.5}>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(Math.max(progress, 0), 100)} 
              color={color}
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: alpha(getColorValue(color), 0.15)
              }}
            />
          </Box>
        )}
        
        {/* Status chips */}
        {chips.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={0.75} mt={1}>
            {chips.map((chip, index) => (
              <Chip
                key={index}
                label={chip.label}
                size="small"
                icon={chip.icon}
                color={chip.color || 'default'}
                sx={{ 
                  height: 26, 
                  fontSize: '0.75rem',
                  fontWeight: 'medium'
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default EsdlMetricCard; 